import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "wouter";
import {
  ArrowRight, ChevronDown, Linkedin, Youtube,
  Menu, X,
  Globe, Share2, QrCode, ScanLine,
  User, MapPin, Clock, ShoppingBasket,
  MessageSquare, Smile, AlertTriangle, MessageCircleMore, Languages,
  Store, AlertCircle, Map, Home, Grid3x3,
  Target, Gamepad2, Receipt, TrendingUp,
  Shield, ShieldCheck, FileText, AlertOctagon, BarChart2,
  Mail, Package, PoundSterling,
  Sparkles, BarChart3,
  Instagram, Tv, GraduationCap, MessageCircle, Trophy, Coins,
} from "lucide-react";
import {
  LANGUAGES,
  COPY,
  TICKER_STATS,
  type LandingLanguage,
  type LandingCopy,
} from "./landing-copy";
import {
  PLATFORM_ORDER,
  MODULE_ORDER,
  getPlatformInfo,
  getModuleInfo,
  type InfoEntry,
} from "./landing-info-content";
import { LANDING_STYLES } from "./landing-styles";

// ─────────────────────────────────────────────────────────────────────────────
// Nav (§6.1)
// ─────────────────────────────────────────────────────────────────────────────

function Nav({
  lang, onLangChange, onRegister, onBookDemo, onInfoOpen, copy,
}: {
  lang: LandingLanguage;
  onLangChange: (l: LandingLanguage) => void;
  onRegister: () => void;
  onBookDemo: () => void;
  /**
   * Mirrors the Footer pattern — clicking a Platform or Modules nav-dropdown
   * item opens the same InfoModal the footer uses, so users get to the
   * paragraph content without scrolling all the way down.
   */
  onInfoOpen: (entry: InfoEntry) => void;
  copy: LandingCopy;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  // Desktop nav: which top-level dropdown (Platform / Modules) is open.
  // Null when none. Click-outside / Escape close it via the same handler
  // already in place for the language menu.
  const [openDropdown, setOpenDropdown] = useState<null | "platform" | "modules">(null);
  // Mobile nav: same idea but renders inline-expanded (not popover) since
  // the mobile overlay has unlimited vertical room and a popover above the
  // touch target would feel clunky on phones.
  const [mobileExpanded, setMobileExpanded] = useState<null | "platform" | "modules">(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dropdownsRef = useRef<HTMLDivElement>(null);
  const langMeta = useMemo(() => LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[3], [lang]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current && !wrapRef.current.contains(t)) setLangOpen(false);
      if (dropdownsRef.current && !dropdownsRef.current.contains(t)) setOpenDropdown(null);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setLangOpen(false);
      setOpenDropdown(null);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  // Lock body scroll when the mobile overlay is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const closeMobile = () => { setMobileOpen(false); setMobileLangOpen(false); };

  return (
    <>
      <nav className="lp-nav" data-scrolled={scrolled} dir="ltr">
        <div className="lp-nav-inner">
          <a href="/" className="lp-logo" aria-label="TalkPack by ShopAI — home">
            <img
              src="/shopai-logo.png"
              alt="ShopAI"
              className="lp-logo-shopai"
              width={2106}
              height={400}
            />
            <span className="lp-logo-sep" aria-hidden="true" />
            <span className="lp-logo-stack">
              <span className="lp-logo-mark">TalkPack</span>
              <span className="lp-logo-sub">Pic-n-Mix</span>
            </span>
          </a>

          <div className="lp-nav-group-right">
            <div className="lp-nav-links" ref={dropdownsRef}>
              {/*
                Platform + Modules nav items are now click-to-open dropdowns.
                Each dropdown lists the same entries as the corresponding
                footer column (TalkPack/Retailer Companion/… in Platform;
                Pic-n-Mix/Learn/Play/… in Modules) and clicking an entry
                opens the InfoModal — same path as the footer click. Use
                Cases was removed for v3.1; will return when content lands.
              */}
              <div className="lp-nav-link-wrap">
                <button
                  type="button"
                  className="lp-nav-link lp-nav-link--has-dropdown"
                  aria-haspopup="menu"
                  aria-expanded={openDropdown === "platform"}
                  onClick={() => setOpenDropdown(d => d === "platform" ? null : "platform")}
                  data-testid="nav-platform-toggle"
                >
                  {copy.navPlatform}
                  <ChevronDown size={14} className="chev" aria-hidden="true" />
                </button>
                {openDropdown === "platform" && (
                  <div className="lp-nav-dropdown" role="menu" aria-label="Platform">
                    {PLATFORM_ORDER.map(key => {
                      const entry = getPlatformInfo(key, lang);
                      return (
                        <button
                          key={key}
                          type="button"
                          role="menuitem"
                          className="lp-nav-dropdown-item"
                          onClick={() => { setOpenDropdown(null); onInfoOpen(entry); }}
                          data-testid={`nav-platform-${key}`}
                        >
                          {entry.title}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="lp-nav-link-wrap">
                <button
                  type="button"
                  className="lp-nav-link lp-nav-link--has-dropdown"
                  aria-haspopup="menu"
                  aria-expanded={openDropdown === "modules"}
                  onClick={() => setOpenDropdown(d => d === "modules" ? null : "modules")}
                  data-testid="nav-modules-toggle"
                >
                  {copy.navModules}
                  <ChevronDown size={14} className="chev" aria-hidden="true" />
                </button>
                {openDropdown === "modules" && (
                  <div className="lp-nav-dropdown" role="menu" aria-label="Modules">
                    {MODULE_ORDER.map(key => {
                      const entry = getModuleInfo(key, lang);
                      return (
                        <button
                          key={key}
                          type="button"
                          role="menuitem"
                          className="lp-nav-dropdown-item"
                          onClick={() => { setOpenDropdown(null); onInfoOpen(entry); }}
                          data-testid={`nav-modules-${key}`}
                        >
                          {entry.title}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <Link className="lp-nav-link" href="/signin">{copy.navPortal}</Link>
            </div>

            <div className="lp-nav-right" ref={wrapRef} style={{ position: "relative" }}>
              <button
                type="button" className="lp-lang-btn"
                onClick={() => setLangOpen((v) => !v)}
                aria-expanded={langOpen} aria-haspopup="listbox"
                data-testid="nav-lang-button"
              >
                <span aria-hidden="true" style={{ fontSize: 12 }}>🌐</span>
                <span>{langMeta.native}</span>
                <ChevronDown size={14} className="chev" aria-hidden="true" />
              </button>
              {langOpen && (
                <div className="lp-lang-menu" role="listbox" aria-label="Select language">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button" role="option"
                      aria-selected={l.code === lang}
                      data-active={l.code === lang}
                      className="lp-lang-menu-item"
                      onClick={() => { onLangChange(l.code); setLangOpen(false); }}
                      lang={l.code === "en-us" ? "en" : l.code}
                      dir={l.dir}
                      data-testid={`lang-${l.code}`}
                    >
                      <span className="native">{l.native}</span>
                      <span className="label">{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button" className="lp-register-link"
                onClick={onRegister} data-testid="nav-register"
              >
                {copy.navRegister}
              </button>
              <Link
                href="/signin" className="lp-cta"
                data-testid="nav-login"
              >
                {copy.navLogin}
                <ArrowRight size={16} className="arrow" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Mobile hamburger — shown only below 768px via CSS. */}
          <button
            type="button"
            className="lp-nav-mobile-toggle"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            aria-controls="lp-mobile-overlay"
            data-testid="nav-mobile-toggle"
          >
            <Menu size={24} aria-hidden="true" />
          </button>
        </div>
      </nav>

      {/* Mobile overlay — full-screen menu for <768px. */}
      {mobileOpen && (
        <div
          id="lp-mobile-overlay"
          className="lp-mobile-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
        >
          <div className="lp-mobile-overlay-head">
            <a href="/" className="lp-logo lp-logo--overlay" aria-label="TalkPack by ShopAI — home" onClick={closeMobile}>
              <img
                src="/shopai-logo.png"
                alt="ShopAI"
                className="lp-logo-shopai"
                width={2106}
                height={400}
              />
              <span className="lp-logo-sep" aria-hidden="true" />
              <span className="lp-logo-stack">
                <span className="lp-logo-mark">TalkPack</span>
              </span>
            </a>
            <button
              type="button"
              className="lp-nav-mobile-toggle"
              onClick={closeMobile}
              aria-label="Close menu"
              data-testid="nav-mobile-close"
            >
              <X size={24} aria-hidden="true" />
            </button>
          </div>

          <div className="lp-mobile-overlay-body">
            <nav className="lp-mobile-links" aria-label="Primary">
              {/*
                Mobile mirrors the desktop dropdown pattern but renders
                inline (no popover) — taps on Platform / Modules expand a
                nested list directly below; tapping an item closes the
                whole overlay and opens the InfoModal.
              */}
              <button
                type="button"
                className="lp-mobile-link lp-mobile-link--has-children"
                aria-expanded={mobileExpanded === "platform"}
                onClick={() => setMobileExpanded(v => v === "platform" ? null : "platform")}
              >
                <span>{copy.navPlatform}</span>
                <ChevronDown
                  size={18}
                  aria-hidden="true"
                  style={{ transform: mobileExpanded === "platform" ? "rotate(180deg)" : "none", transition: "transform 200ms" }}
                />
              </button>
              {mobileExpanded === "platform" && (
                <div className="lp-mobile-sublist" role="menu">
                  {PLATFORM_ORDER.map(key => {
                    const entry = getPlatformInfo(key, lang);
                    return (
                      <button
                        key={key}
                        type="button"
                        role="menuitem"
                        className="lp-mobile-sublist-item"
                        onClick={() => { closeMobile(); setMobileExpanded(null); onInfoOpen(entry); }}
                      >
                        {entry.title}
                      </button>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                className="lp-mobile-link lp-mobile-link--has-children"
                aria-expanded={mobileExpanded === "modules"}
                onClick={() => setMobileExpanded(v => v === "modules" ? null : "modules")}
              >
                <span>{copy.navModules}</span>
                <ChevronDown
                  size={18}
                  aria-hidden="true"
                  style={{ transform: mobileExpanded === "modules" ? "rotate(180deg)" : "none", transition: "transform 200ms" }}
                />
              </button>
              {mobileExpanded === "modules" && (
                <div className="lp-mobile-sublist" role="menu">
                  {MODULE_ORDER.map(key => {
                    const entry = getModuleInfo(key, lang);
                    return (
                      <button
                        key={key}
                        type="button"
                        role="menuitem"
                        className="lp-mobile-sublist-item"
                        onClick={() => { closeMobile(); setMobileExpanded(null); onInfoOpen(entry); }}
                      >
                        {entry.title}
                      </button>
                    );
                  })}
                </div>
              )}

              <Link href="/signin" onClick={closeMobile} className="lp-mobile-link">{copy.navPortal}</Link>
            </nav>

            <div className="lp-mobile-divider" aria-hidden="true" />

            <div className="lp-mobile-lang">
              <button
                type="button"
                className="lp-mobile-lang-btn"
                onClick={() => setMobileLangOpen((v) => !v)}
                aria-expanded={mobileLangOpen}
              >
                <Globe size={16} aria-hidden="true" />
                <span>{langMeta.native}</span>
                <span className="muted">· {langMeta.label}</span>
                <ChevronDown
                  size={16}
                  aria-hidden="true"
                  style={{ marginInlineStart: "auto", transform: mobileLangOpen ? "rotate(180deg)" : "none", transition: "transform 200ms" }}
                />
              </button>
              {mobileLangOpen && (
                <div className="lp-mobile-lang-list" role="listbox">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      role="option"
                      aria-selected={l.code === lang}
                      data-active={l.code === lang}
                      onClick={() => { onLangChange(l.code); setMobileLangOpen(false); }}
                      lang={l.code === "en-us" ? "en" : l.code}
                      dir={l.dir}
                    >
                      <span className="native">{l.native}</span>
                      <span className="label">{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="lp-mobile-ctas">
              <button
                type="button"
                className="lp-mobile-cta lp-mobile-cta--primary"
                onClick={() => { closeMobile(); onBookDemo(); }}
              >
                {copy.navBookDemo}
                <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="lp-mobile-cta lp-mobile-cta--secondary"
                onClick={() => { closeMobile(); onRegister(); }}
              >
                {copy.navRegister}
              </button>
              <Link
                href="/signin"
                className="lp-mobile-cta lp-mobile-cta--ghost"
                onClick={closeMobile}
              >
                {copy.navLogin}
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Trust strip (§6.2)
// ─────────────────────────────────────────────────────────────────────────────

function TrustStrip({ copy }: { copy: LandingCopy }) {
  return (
    <div className="lp-trust">
      <div className="lp-trust-inner">
        <span className="lp-trust-right">{copy.trustRight}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero (§6.3) — 60/40 split, illustrated visual on right
// ─────────────────────────────────────────────────────────────────────────────

function HeroVisualPhoto({ copy }: { copy: LandingCopy }) {
  return (
    <figure className="lp-hero-visual lp-hero-visual-photo">
      <img
        src="/hero-shopper.jpg?v=3"
        alt={copy.heroAlt}
        loading="eager"
        fetchPriority="high"
      />
    </figure>
  );
}

function HeroVisualSVG({ copy }: { copy: LandingCopy }) {
  return (
    <div className="lp-hero-visual" aria-hidden="false" role="img" aria-label="Shopper scanning a product with TalkPack">
      <svg viewBox="0 0 600 600" xmlns="http://www.w3.org/2000/svg" className="lp-hero-svg">
        <defs>
          <radialGradient id="glow" cx="50%" cy="55%" r="50%">
            <stop offset="0%" stopColor="#E8542B" stopOpacity="0.42" />
            <stop offset="60%" stopColor="#E8542B" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#E8542B" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="boxTop" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F5C44B" />
            <stop offset="100%" stopColor="#E29A1E" />
          </linearGradient>
          <linearGradient id="bottleFront" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#16A34A" />
            <stop offset="100%" stopColor="#0F7A38" />
          </linearGradient>
        </defs>

        {/* Background glow */}
        <circle cx="300" cy="330" r="260" fill="url(#glow)" />

        {/* Shelf line */}
        <line x1="40" y1="450" x2="560" y2="450" stroke="#E4DFD5" strokeWidth="2" />
        <line x1="40" y1="452" x2="560" y2="452" stroke="#F5A524" strokeWidth="1" opacity="0.35" />

        {/* Bottle (left) */}
        <g transform="translate(90 210)">
          {/* neck */}
          <rect x="34" y="0" width="22" height="24" fill="#0B0B0F" rx="2" />
          <rect x="30" y="20" width="30" height="12" fill="#0B0B0F" rx="2" />
          {/* body */}
          <path d="M20 36 Q20 28 38 28 L52 28 Q70 28 70 36 L70 240 Q70 248 60 248 L30 248 Q20 248 20 240 Z"
            fill="url(#bottleFront)" stroke="#0B0B0F" strokeWidth="2" strokeLinejoin="round" />
          {/* label */}
          <rect x="24" y="96" width="42" height="90" fill="#FFFFFF" stroke="#0B0B0F" strokeWidth="1.5" rx="2" />
          <rect x="30" y="108" width="30" height="6" fill="#0B0B0F" />
          <rect x="30" y="124" width="22" height="4" fill="#6B6B73" />
          <rect x="30" y="134" width="28" height="4" fill="#6B6B73" />
          <rect x="30" y="144" width="18" height="4" fill="#6B6B73" />
          <rect x="30" y="164" width="24" height="14" fill="#E8542B" />
        </g>

        {/* Cereal box (centre, being scanned) */}
        <g transform="translate(236 196)">
          {/* box body */}
          <rect x="0" y="0" width="152" height="254" fill="url(#boxTop)" stroke="#0B0B0F" strokeWidth="2" rx="3" />
          {/* brand band */}
          <rect x="0" y="42" width="152" height="26" fill="#0B0B0F" />
          <text x="76" y="61" textAnchor="middle" fill="#F5C44B" fontSize="18" fontWeight="800" fontFamily="Poppins, sans-serif" letterSpacing="-0.5">
            GOOD O'S
          </text>
          {/* abstract cereal illustration */}
          <circle cx="76" cy="118" r="26" fill="#F5A524" stroke="#0B0B0F" strokeWidth="2" />
          <circle cx="76" cy="118" r="10" fill="#F5C44B" />
          <circle cx="58" cy="132" r="14" fill="#B83E1B" stroke="#0B0B0F" strokeWidth="2" />
          <circle cx="94" cy="132" r="14" fill="#B83E1B" stroke="#0B0B0F" strokeWidth="2" />
          <circle cx="76" cy="144" r="14" fill="#F5A524" stroke="#0B0B0F" strokeWidth="2" />
          {/* spoon/milk doodle */}
          <path d="M36 174 Q76 186 118 174" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" />
          {/* QR code on pack */}
          <rect x="18" y="200" width="40" height="40" fill="#FFFFFF" stroke="#0B0B0F" strokeWidth="1.5" />
          <rect x="22" y="204" width="8" height="8" fill="#0B0B0F" />
          <rect x="46" y="204" width="8" height="8" fill="#0B0B0F" />
          <rect x="22" y="228" width="8" height="8" fill="#0B0B0F" />
          <rect x="34" y="216" width="4" height="4" fill="#0B0B0F" />
          <rect x="42" y="220" width="4" height="4" fill="#0B0B0F" />
          <rect x="46" y="228" width="4" height="4" fill="#0B0B0F" />
          <text x="104" y="224" fill="#0B0B0F" fontSize="8" fontWeight="700" fontFamily="Poppins, sans-serif">SCAN ME</text>
        </g>

        {/* Confectionery bar (right) */}
        <g transform="translate(420 328)">
          <rect x="0" y="0" width="130" height="122" fill="#B83E1B" stroke="#0B0B0F" strokeWidth="2" rx="4" />
          <rect x="0" y="24" width="130" height="16" fill="#F5C44B" />
          <text x="65" y="37" textAnchor="middle" fill="#0B0B0F" fontSize="10" fontWeight="800" fontFamily="Poppins, sans-serif" letterSpacing="0.5">
            CRUNCH BAR
          </text>
          {/* wavy wrapper fold */}
          <path d="M0 54 Q32 60 65 54 T130 54" stroke="#0B0B0F" strokeWidth="1.5" fill="none" />
          <circle cx="26" cy="84" r="8" fill="#F5C44B" />
          <circle cx="50" cy="88" r="6" fill="#F5C44B" />
          <circle cx="76" cy="82" r="10" fill="#F5C44B" />
          <circle cx="100" cy="90" r="7" fill="#F5C44B" />
        </g>

        {/* Smartphone with scan reticle (bottom-right) */}
        <g transform="translate(336 292)">
          {/* phone body */}
          <rect x="0" y="0" width="150" height="268" fill="#0B0B0F" stroke="#0B0B0F" strokeWidth="1" rx="22" />
          <rect x="6" y="6" width="138" height="256" fill="#26262C" rx="16" />
          {/* notch */}
          <rect x="55" y="10" width="40" height="6" fill="#0B0B0F" rx="3" />
          {/* screen content — scan reticle */}
          <rect x="20" y="36" width="110" height="160" fill="#F7F4EE" rx="6" />
          {/* reticle corners */}
          <g stroke="#E8542B" strokeWidth="3" fill="none" strokeLinecap="round" className="lp-reticle">
            <path d="M 38 62 L 38 52 L 48 52" />
            <path d="M 102 52 L 112 52 L 112 62" />
            <path d="M 112 170 L 112 180 L 102 180" />
            <path d="M 48 180 L 38 180 L 38 170" />
          </g>
          {/* tiny product preview inside reticle */}
          <rect x="60" y="74" width="30" height="84" fill="#F5C44B" stroke="#0B0B0F" strokeWidth="1" rx="1" />
          <rect x="60" y="92" width="30" height="6" fill="#0B0B0F" />
          {/* status chip */}
          <rect x="22" y="204" width="106" height="22" fill="#E6F6EF" stroke="#00A86B" strokeWidth="1" rx="11" />
          <circle cx="34" cy="215" r="4" fill="#00A86B" />
          <text x="44" y="219" fill="#00A86B" fontSize="10" fontWeight="700" fontFamily="Poppins, sans-serif">Product verified</text>
          {/* nav dots */}
          <circle cx="75" cy="252" r="3" fill="#6B6B73" />
          <circle cx="87" cy="252" r="3" fill="#E8542B" />
          <circle cx="99" cy="252" r="3" fill="#6B6B73" />
        </g>

        {/* Floating data pills */}
        <g className="lp-pill lp-pill-1">
          <rect x="64" y="110" width="108" height="34" rx="17" fill="#FFFFFF" stroke="#0B0B0F" strokeWidth="1.5" />
          <circle cx="82" cy="127" r="5" fill="#E8542B" />
          <text x="94" y="131" fill="#0B0B0F" fontSize="13" fontWeight="600" fontFamily="Poppins, sans-serif">{copy.heroPillAllergens}</text>
        </g>
        <g className="lp-pill lp-pill-2">
          <rect x="180" y="64" width="84" height="34" rx="17" fill="#FFFFFF" stroke="#0B0B0F" strokeWidth="1.5" />
          <circle cx="198" cy="81" r="5" fill="#F5A524" />
          <text x="210" y="85" fill="#0B0B0F" fontSize="13" fontWeight="600" fontFamily="Poppins, sans-serif">{copy.heroPillHfss}</text>
        </g>
        <g className="lp-pill lp-pill-3">
          <rect x="28" y="54" width="128" height="34" rx="17" fill="#FFFFFF" stroke="#0B0B0F" strokeWidth="1.5" />
          <circle cx="46" cy="71" r="5" fill="#16A34A" />
          <text x="58" y="75" fill="#0B0B0F" fontSize="13" fontWeight="600" fontFamily="Poppins, sans-serif">{copy.heroPillIngredients}</text>
        </g>

        {/* Verified chip near phone bottom-right */}
        <g transform="translate(356 566)" className="lp-verified-chip">
          <rect x="0" y="0" width="124" height="28" rx="14" fill="#00A86B" />
          <text x="36" y="19" fill="#FFFFFF" fontSize="12" fontWeight="700" fontFamily="Poppins, sans-serif">
            {copy.heroVerified}
          </text>
          <path d="M 16 14 L 20 18 L 26 10" stroke="#FFFFFF" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </svg>
    </div>
  );
}

function Hero({ copy, onBookDemo, onSeeHow }: {
  copy: LandingCopy; onBookDemo: () => void; onSeeHow: () => void;
}) {
  return (
    <section className="lp-hero" id="platform">
      <div className="lp-hero-inner">
        <div className="lp-hero-text">
          <h1 className="lp-hero-title">
            <span className="ink">{copy.heroL1}</span>
            <br />
            <span className="accent">{copy.heroL2}</span>
          </h1>
          <p className="lp-hero-sub">{copy.heroSub}</p>
          <div className="lp-hero-ctas">
            <button type="button" className="lp-cta lp-cta-lg" onClick={onBookDemo} data-testid="hero-bookdemo">
              {copy.heroCtaPrimary}
              <ArrowRight size={18} className="arrow" aria-hidden="true" />
            </button>
            <button type="button" className="lp-cta-ghost" onClick={onSeeHow} data-testid="hero-seehow">
              {copy.heroCtaGhost}
              <ArrowRight size={18} className="arrow" aria-hidden="true" />
            </button>
          </div>
        </div>
        <HeroVisualPhoto copy={copy} />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stats ticker (§6.4)
// ─────────────────────────────────────────────────────────────────────────────

function StatsTicker({ copy }: { copy: LandingCopy }) {
  const loop = [...TICKER_STATS, ...TICKER_STATS];
  const trackRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const pxPerSecond = 60;
    let raf = 0;
    let last = performance.now();
    let offset = 0;
    let halfWidth = track.scrollWidth / 2;
    const recalc = () => { halfWidth = track.scrollWidth / 2; };
    const ro = new ResizeObserver(recalc);
    ro.observe(track);
    const tick = (now: number) => {
      const dt = Math.min(64, now - last);
      last = now;
      offset += (dt / 1000) * pxPerSecond;
      if (halfWidth > 0 && offset >= halfWidth) offset -= halfWidth;
      track.style.transform = `translateX(${-offset}px)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  return (
    <div className="lp-ticker" aria-label="Platform facts">
      <div className="lp-ticker-track" ref={trackRef}>
        {loop.map((s, i) => (
          <span className="lp-ticker-item" key={i} aria-hidden={i >= TICKER_STATS.length ? true : undefined}>
            <span className="lp-ticker-value">{s.value}</span>
            <span className="lp-ticker-label">{copy[s.labelKey]}</span>
            <span className="lp-ticker-sep" aria-hidden="true" />
          </span>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Full Journey infographic (§6.5)
// ─────────────────────────────────────────────────────────────────────────────

type DashTile = {
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number; "aria-hidden"?: boolean }>;
  title: string;
  desc: string;
  Viz?: React.ComponentType<{ copy: LandingCopy }>;
};
type DashSection = { label: string; shortLabel: string; accent: string; tiles: DashTile[] };

/* ── Conversation Intelligence visualisations ── */

function VizQuestionDonut({ copy }: { copy: LandingCopy }) {
  const segments = [
    { label: copy.vizQAllergens,   pct: 34, color: "#00D9A3" },
    { label: copy.vizQIngredients, pct: 28, color: "#00B88A" },
    { label: copy.vizQNutrition,   pct: 22, color: "#0E9676" },
    { label: copy.vizQRecipes,     pct: 12, color: "#0E6B56" },
    { label: copy.vizQUsage,       pct: 4,  color: "#0A5E4A" },
  ];
  const R = 18;
  const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <div className="lp-viz lp-viz-donut">
      <div className="lp-viz-donut-row">
        <svg viewBox="0 0 48 48" className="lp-viz-donut-svg" aria-hidden="true">
          <circle cx="24" cy="24" r={R} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
          {segments.map((s) => {
            const len = (s.pct / 100) * C;
            const el = (
              <circle
                key={s.label}
                cx="24" cy="24" r={R}
                fill="none" stroke={s.color} strokeWidth="6"
                strokeDasharray={`${len} ${C - len}`}
                strokeDashoffset={-offset}
                transform="rotate(-90 24 24)"
              />
            );
            offset += len;
            return el;
          })}
        </svg>
        <ul className="lp-viz-donut-legend">
          {segments.map((s) => (
            <li key={s.label}>
              <span className="dot" style={{ background: s.color }} />
              {s.label}
              <strong>{s.pct}%</strong>
            </li>
          ))}
        </ul>
      </div>
      <div className="lp-viz-cap">{copy.vizCapQuestions}</div>
    </div>
  );
}

function VizSentimentBar({ copy }: { copy: LandingCopy }) {
  const segments = [
    { label: copy.vizSentPositive, pct: 89, color: "#00D9A3" },
    { label: copy.vizSentNeutral,  pct: 7,  color: "#F5A524" },
    { label: copy.vizSentNegative, pct: 4,  color: "#E11D48" },
  ];
  return (
    <div className="lp-viz lp-viz-sentiment">
      <div className="lp-viz-sentiment-labels">
        {segments.map((s) => (
          <span key={s.label} style={{ flex: s.pct, color: s.color }}>{s.pct}%</span>
        ))}
      </div>
      <div className="lp-viz-sentiment-bar">
        {segments.map((s) => (
          <span key={s.label} style={{ width: `${s.pct}%`, background: s.color }} />
        ))}
      </div>
      <div className="lp-viz-sentiment-legend">
        {segments.map((s) => <span key={s.label}>{s.label}</span>)}
      </div>
      <div className="lp-viz-cap">{copy.vizCapSentiment}</div>
    </div>
  );
}

function VizConfusionBubbles({ copy }: { copy: LandingCopy }) {
  const bubbles = [
    copy.vizConfusion1,
    copy.vizConfusion2,
    copy.vizConfusion3,
  ];
  return (
    <div className="lp-viz lp-viz-bubbles">
      {bubbles.map((text, i) => (
        <span
          key={text}
          className="lp-viz-bubble"
          style={{ animationDelay: `${i * 0.5}s` }}
        >
          {text}
        </span>
      ))}
      <div className="lp-viz-cap">{copy.vizCapConfusion}</div>
    </div>
  );
}

function VizPurchaseFunnel({ copy }: { copy: LandingCopy }) {
  const stages = [
    { label: copy.vizFunnelCurious,    pct: 100 },
    { label: copy.vizFunnelInterested, pct: 62  },
    { label: copy.vizFunnelReady,      pct: 34  },
  ];
  return (
    <div className="lp-viz lp-viz-funnel">
      {stages.map((s) => (
        <div key={s.label} className="lp-viz-funnel-row">
          <span className="lp-viz-funnel-label">{s.label}</span>
          <span className="lp-viz-funnel-track">
            <span className="lp-viz-funnel-bar" style={{ width: `${s.pct}%` }} />
          </span>
          <span className="lp-viz-funnel-pct">{s.pct}%</span>
        </div>
      ))}
      <div className="lp-viz-cap">{copy.vizCapFunnel}</div>
    </div>
  );
}

/* ── Distribution Intelligence visualisations ── */

function VizStoreAvailability({ copy }: { copy: LandingCopy }) {
  const pct = 94;
  const R = 22;
  const C = 2 * Math.PI * R;
  const fill = (pct / 100) * C;
  return (
    <div className="lp-viz lp-viz-availability">
      <div className="lp-viz-avail-ring">
        <svg viewBox="0 0 56 56" aria-hidden="true">
          <circle cx="28" cy="28" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
          <circle
            cx="28" cy="28" r={R}
            fill="none" stroke="#00D9A3" strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${fill} ${C}`}
            transform="rotate(-90 28 28)"
          />
        </svg>
        <span className="lp-viz-avail-pct">{pct}%</span>
      </div>
      <div className="lp-viz-avail-small">{copy.vizAvailDetail}</div>
      <div className="lp-viz-cap">{copy.vizCapAvail}</div>
    </div>
  );
}

function VizOOSAlerts({ copy }: { copy: LandingCopy }) {
  const rows = [
    { store: "Nisa Leeds LS1", t: "2h" },
    { store: "Spar Bristol BS4", t: "4h" },
    { store: "Premier Hull HU3", t: "1d" },
  ];
  return (
    <div className="lp-viz lp-viz-oos">
      {rows.map((r) => (
        <div key={r.store} className="lp-viz-oos-row">
          <span className="lp-viz-oos-dot" />
          <span className="lp-viz-oos-store">{r.store}</span>
          <span className="lp-viz-oos-time">{r.t}</span>
        </div>
      ))}
      <div className="lp-viz-cap">{copy.vizCapOos}</div>
    </div>
  );
}

function VizHeatmap({ copy }: { copy: LandingCopy }) {
  // Reuses simplified UK outline with color-graded regions.
  return (
    <div className="lp-viz lp-viz-heatmap">
      <svg viewBox="0 0 70 100" aria-hidden="true" className="lp-viz-map-svg">
        <defs>
          <linearGradient id="lp-heat-london" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00F5B3" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#00F5B3" stopOpacity="0.45" />
          </linearGradient>
        </defs>
        {/* Base outline */}
        <path
          d="M 34 8 L 46 6 L 56 14 L 54 24 L 58 30 L 52 38 L 56 48 L 48 58 L 54 64 L 50 72 L 46 78 L 50 86 L 46 94 L 40 96 L 32 90 L 26 80 L 18 78 L 22 70 L 26 62 L 18 58 L 20 48 L 24 40 L 22 32 L 26 22 L 24 16 L 30 12 Z"
          fill="rgba(255, 255, 255, 0.04)"
          stroke="rgba(0, 201, 167, 0.4)"
          strokeWidth="0.6"
        />
        {/* Regional heat blobs */}
        <circle cx="52" cy="78" r="10" fill="url(#lp-heat-london)" />
        <circle cx="38" cy="52" r="8"  fill="#00D9A3" opacity="0.5" />
        <circle cx="42" cy="63" r="7"  fill="#00D9A3" opacity="0.4" />
        <circle cx="45" cy="46" r="6"  fill="#00D9A3" opacity="0.35" />
        <circle cx="38" cy="26" r="5"  fill="#00D9A3" opacity="0.2" />
        <circle cx="36" cy="74" r="5"  fill="#00D9A3" opacity="0.28" />
      </svg>
      <div className="lp-viz-cap">{copy.vizCapHeat}</div>
    </div>
  );
}

function VizHouseholdGrid({ copy }: { copy: LandingCopy }) {
  const total = 25;
  const highlighted = 17;
  return (
    <div className="lp-viz lp-viz-household">
      <div className="lp-viz-house-grid">
        {Array.from({ length: total }).map((_, i) => {
          const on = i < highlighted;
          return (
            <svg
              key={i}
              viewBox="0 0 16 16"
              className="lp-viz-house"
              data-on={on}
              aria-hidden="true"
            >
              <path
                d="M 2 8 L 8 3 L 14 8 L 14 14 L 10 14 L 10 10 L 6 10 L 6 14 L 2 14 Z"
                fill="none"
                strokeWidth="1.3"
                strokeLinejoin="round"
              />
            </svg>
          );
        })}
      </div>
      <div className="lp-viz-cap">{copy.vizCapHousehold}</div>
    </div>
  );
}

function VizFasciaBars({ copy }: { copy: LandingCopy }) {
  // Retailer fascia names (Nisa/Spar/Co-op/Premier) are UK retail brands — kept literal.
  const bars = [
    { label: "Nisa",    h: 90 },
    { label: "Spar",    h: 75 },
    { label: "Co-op",   h: 62 },
    { label: "Premier", h: 45 },
  ];
  return (
    <div className="lp-viz lp-viz-bars lp-viz-fascia">
      <div className="lp-viz-bars-chart" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
        {bars.map((b) => (
          <div key={b.label} className="lp-viz-bar-col">
            <span className="lp-viz-bar" style={{ ["--h" as string]: `${b.h}%` }} />
            <span className="lp-viz-bar-label">{b.label}</span>
          </div>
        ))}
      </div>
      <div className="lp-viz-cap">{copy.vizCapFascia}</div>
    </div>
  );
}

/* ── Campaign Performance visualisations ── */

function VizEngagementRate({ copy }: { copy: LandingCopy }) {
  return (
    <div className="lp-viz lp-viz-engrate">
      <div className="lp-viz-engrate-num">78%</div>
      <div className="lp-viz-engrate-compare">
        <span className="up" aria-hidden="true">▲</span>
        <span>{copy.vizEngrateCompare}</span>
      </div>
      <div className="lp-viz-cap">{copy.vizCapEngrate}</div>
    </div>
  );
}

function VizGameFunnel({ copy }: { copy: LandingCopy }) {
  const stages = [
    { label: copy.vizGameStart, pct: 100, color: "#0A5E4A" },
    { label: copy.vizGamePlay,  pct: 92,  color: "#0E9676" },
    { label: copy.vizGameWin,   pct: 72,  color: "#00C896" },
    { label: copy.vizGameClaim, pct: 64,  color: "#00F5B3" },
  ];
  return (
    <div className="lp-viz lp-viz-gamefunnel">
      {stages.map((s) => (
        <div key={s.label} className="lp-viz-gf-row">
          <span className="lp-viz-gf-label">{s.label}</span>
          <span className="lp-viz-gf-track">
            <span className="lp-viz-gf-bar" style={{ width: `${s.pct}%`, background: s.color }} />
          </span>
          <span className="lp-viz-gf-pct">{s.pct}%</span>
        </div>
      ))}
      <div className="lp-viz-cap">{copy.vizCapGame}</div>
    </div>
  );
}

function VizRedemption({ copy }: { copy: LandingCopy }) {
  const blocks = [
    { num: "847", label: copy.vizRdmClaimed   },
    { num: "812", label: copy.vizRdmValidated },
    { num: "35",  label: copy.vizRdmExpired   },
  ];
  return (
    <div className="lp-viz lp-viz-redemption">
      <div className="lp-viz-rdm-row">
        {blocks.map((b) => (
          <div key={b.label} className="lp-viz-rdm-block">
            <div className="lp-viz-rdm-num">{b.num}</div>
            <div className="lp-viz-rdm-label">{b.label}</div>
          </div>
        ))}
      </div>
      <div className="lp-viz-cap">{copy.vizCapRdm}</div>
    </div>
  );
}

function VizROIDashboard({ copy }: { copy: LandingCopy }) {
  return (
    <div className="lp-viz lp-viz-roi">
      <div className="lp-viz-roi-row">
        <div className="lp-viz-roi-col">
          <div className="lp-viz-roi-num">£14,200</div>
          <div className="lp-viz-roi-label">{copy.vizRoiSpend}</div>
        </div>
        <span className="lp-viz-roi-arrow" aria-hidden="true">→</span>
        <div className="lp-viz-roi-col">
          <div className="lp-viz-roi-num lp-viz-roi-num--bright">£48,600</div>
          <div className="lp-viz-roi-label">{copy.vizRoiValueLabel}</div>
        </div>
      </div>
      <div className="lp-viz-roi-return">{copy.vizRoiReturn}</div>
      <div className="lp-viz-cap">{copy.vizCapRoi}</div>
    </div>
  );
}

/* ── Compliance & Audit visualisations ── */

function VizComplianceScore({ copy }: { copy: LandingCopy }) {
  const pct = 98.4;
  const R = 22;
  const C = 2 * Math.PI * R;
  const fill = (pct / 100) * C;
  return (
    <div className="lp-viz lp-viz-compliance">
      <div className="lp-viz-comp-ring">
        <svg viewBox="0 0 56 56" aria-hidden="true">
          <circle cx="28" cy="28" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
          <circle
            cx="28" cy="28" r={R}
            fill="none" stroke="#00A86B" strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={`${fill} ${C}`}
            transform="rotate(-90 28 28)"
          />
        </svg>
        <span className="lp-viz-comp-pct">{pct}%</span>
        <span className="lp-viz-comp-tick" aria-hidden="true">
          <svg viewBox="0 0 12 12"><path d="M 2 6 L 5 9 L 10 3" stroke="#00A86B" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </span>
      </div>
      <div className="lp-viz-cap">{copy.vizCapComp}</div>
    </div>
  );
}

function VizAuditLogs({ copy }: { copy: LandingCopy }) {
  return (
    <div className="lp-viz lp-viz-audit">
      <div className="lp-viz-audit-stack" aria-hidden="true">
        <svg viewBox="0 0 36 44" className="lp-viz-audit-doc" style={{ transform: "rotate(-8deg) translateX(-10px)" }}>
          <rect x="2" y="2" width="32" height="40" rx="3" fill="rgba(20,20,25,0.8)" stroke="rgba(0,217,163,0.5)" strokeWidth="1" />
          <line x1="7" y1="12" x2="29" y2="12" stroke="rgba(0,217,163,0.5)" strokeWidth="1" />
          <line x1="7" y1="18" x2="29" y2="18" stroke="rgba(0,217,163,0.3)" strokeWidth="1" />
          <line x1="7" y1="24" x2="24" y2="24" stroke="rgba(0,217,163,0.3)" strokeWidth="1" />
        </svg>
        <svg viewBox="0 0 36 44" className="lp-viz-audit-doc">
          <rect x="2" y="2" width="32" height="40" rx="3" fill="rgba(20,20,25,0.9)" stroke="rgba(0,217,163,0.7)" strokeWidth="1" />
          <line x1="7" y1="12" x2="29" y2="12" stroke="rgba(0,217,163,0.7)" strokeWidth="1" />
          <line x1="7" y1="18" x2="29" y2="18" stroke="rgba(0,217,163,0.5)" strokeWidth="1" />
          <line x1="7" y1="24" x2="24" y2="24" stroke="rgba(0,217,163,0.5)" strokeWidth="1" />
          <line x1="7" y1="30" x2="29" y2="30" stroke="rgba(0,217,163,0.5)" strokeWidth="1" />
        </svg>
        <svg viewBox="0 0 36 44" className="lp-viz-audit-doc" style={{ transform: "rotate(8deg) translateX(10px)" }}>
          <rect x="2" y="2" width="32" height="40" rx="3" fill="rgba(20,20,25,0.8)" stroke="rgba(0,217,163,0.5)" strokeWidth="1" />
          <line x1="7" y1="12" x2="29" y2="12" stroke="rgba(0,217,163,0.5)" strokeWidth="1" />
          <line x1="7" y1="18" x2="29" y2="18" stroke="rgba(0,217,163,0.3)" strokeWidth="1" />
        </svg>
      </div>
      <div className="lp-viz-audit-formats">
        <span className="lp-viz-audit-pill">PDF</span>
        <span className="lp-viz-audit-pill">CSV</span>
        <span className="lp-viz-audit-pill">JSON</span>
      </div>
      <div className="lp-viz-cap">{copy.vizCapAudit}</div>
    </div>
  );
}

function VizViolationZero({ copy }: { copy: LandingCopy }) {
  return (
    <div className="lp-viz lp-viz-zero">
      <div className="lp-viz-zero-num">0</div>
      <span className="lp-viz-zero-tick" aria-hidden="true">
        <svg viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="#00A86B" /><path d="M 4 8 L 7 11 L 12 5" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </span>
      <div className="lp-viz-cap">{copy.vizCapViolation}</div>
    </div>
  );
}

function VizNutritionBars({ copy }: { copy: LandingCopy }) {
  const nutrients = [
    { label: copy.vizNutSugar,  pct: 42, color: "#E11D48" },
    { label: copy.vizNutFat,    pct: 28, color: "#F5A524" },
    { label: copy.vizNutSalt,   pct: 18, color: "#F5A524" },
    { label: copy.vizNutSatFat, pct: 12, color: "#00A86B" },
  ];
  return (
    <div className="lp-viz lp-viz-nutrition">
      {nutrients.map((n) => (
        <div key={n.label} className="lp-viz-nut-row">
          <span className="lp-viz-nut-label">{n.label}</span>
          <span className="lp-viz-nut-track">
            <span className="lp-viz-nut-bar" style={{ width: `${n.pct}%`, background: n.color }} />
          </span>
          <span className="lp-viz-nut-pct">{n.pct}%</span>
        </div>
      ))}
      <div className="lp-viz-cap">{copy.vizCapNut}</div>
    </div>
  );
}

function VizLangPills({ copy }: { copy: LandingCopy }) {
  const langs = [
    { code: "EN",             pct: 68 },
    { code: "ES",             pct: 12 },
    { code: "PL",             pct: 8  },
    { code: "UR",             pct: 6  },
    { code: "AR",             pct: 4  },
    { code: copy.vizLangOther, pct: 2 },
  ];
  return (
    <div className="lp-viz lp-viz-langs">
      <div className="lp-viz-lang-row">
        {langs.map((l) => (
          <span key={l.code} className="lp-viz-lang-pill">
            <strong>{l.code}</strong>
            <span>{l.pct}%</span>
          </span>
        ))}
      </div>
      <div className="lp-viz-cap">{copy.vizCapLang}</div>
    </div>
  );
}

function VizCRMCounter({ copy }: { copy: LandingCopy }) {
  const [count, setCount] = useState(2847);
  const [setIdx, setSetIdx] = useState(0);

  useEffect(() => {
    let id: number;
    const schedule = () => {
      const delay = 2000 + Math.random() * 2000;
      id = window.setTimeout(() => {
        setCount((c) => c + 1);
        schedule();
      }, delay);
    };
    schedule();
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setSetIdx((i) => (i + 1) % 2), 4000);
    return () => window.clearInterval(id);
  }, []);

  const sets = [
    [
      { initial: "A", label: "Ana · SE1" },
      { initial: "J", label: "Jake · M4" },
      { initial: "P", label: "Priya · L3" },
      { initial: "S", label: "Sam · BS1" },
    ],
    [
      { initial: "R", label: "Rob · N12" },
      { initial: "M", label: "Mia · EH3" },
      { initial: "K", label: "Kiran · B5" },
      { initial: "E", label: "Em · W1" },
    ],
  ];
  const current = sets[setIdx];

  return (
    <div className="lp-viz lp-viz-crm">
      <div className="lp-viz-crm-num">{count.toLocaleString()}</div>
      <div className="lp-viz-cap">{copy.vizCapCrm}</div>
      <div key={setIdx} className="lp-viz-crm-row">
        {current.map((a) => (
          <div key={a.initial + a.label} className="lp-viz-crm-avatar">
            <span className="lp-viz-crm-circle">{a.initial}</span>
            <span className="lp-viz-crm-label">{a.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VizUKMap({ copy }: { copy: LandingCopy }) {
  // Approximate city coords inside 70×100 viewBox of simplified UK.
  // City names stay as English proper nouns (London/Birmingham/etc.).
  const cities = [
    { name: "London",     x: 52, y: 78 },
    { name: "Birmingham", x: 42, y: 63 },
    { name: "Manchester", x: 38, y: 50 },
    { name: "Leeds",      x: 45, y: 47 },
    { name: "Bristol",    x: 36, y: 74 },
    { name: "Edinburgh",  x: 38, y: 26 },
  ];
  return (
    <div className="lp-viz lp-viz-map">
      <svg viewBox="0 0 70 100" aria-hidden="true" className="lp-viz-map-svg">
        <path
          d="M 34 8 L 46 6 L 56 14 L 54 24 L 58 30 L 52 38 L 56 48 L 48 58 L 54 64 L 50 72 L 46 78 L 50 86 L 46 94 L 40 96 L 32 90 L 26 80 L 18 78 L 22 70 L 26 62 L 18 58 L 20 48 L 24 40 L 22 32 L 26 22 L 24 16 L 30 12 Z"
          fill="rgba(255, 255, 255, 0.06)"
          stroke="rgba(0, 201, 167, 0.5)"
          strokeWidth="0.6"
        />
        {cities.map((c, i) => (
          <g key={c.name}>
            <circle
              cx={c.x}
              cy={c.y}
              r="4"
              fill="rgba(0, 217, 163, 0.25)"
              className="lp-viz-map-ring"
              style={{ animationDelay: `${i * 0.33}s` }}
            />
            <circle cx={c.x} cy={c.y} r="1.6" fill="#00D9A3" />
          </g>
        ))}
      </svg>
      <div className="lp-viz-cap">{copy.vizCapMap}</div>
    </div>
  );
}

function VizWeeklyBars({ copy }: { copy: LandingCopy }) {
  const bars = [
    { d: copy.vizDayMon, h: 60 },
    { d: copy.vizDayTue, h: 70 },
    { d: copy.vizDayWed, h: 65 },
    { d: copy.vizDayThu, h: 80 },
    { d: copy.vizDayFri, h: 90 },
    { d: copy.vizDaySat, h: 100 },
    { d: copy.vizDaySun, h: 55 },
  ];
  return (
    <div className="lp-viz lp-viz-bars">
      <div className="lp-viz-bars-chart">
        {bars.map((b) => (
          <div key={b.d} className="lp-viz-bar-col">
            <span className="lp-viz-bar" style={{ ["--h" as string]: `${b.h}%` }} />
            <span className="lp-viz-bar-label">{b.d}</span>
          </div>
        ))}
      </div>
      <div className="lp-viz-cap">{copy.vizCapWeek}</div>
    </div>
  );
}

function VizBasketStack({ copy }: { copy: LandingCopy }) {
  const segments = [
    { label: copy.vizBasketDairy,  pct: 40, color: "#00D9A3" },
    { label: copy.vizBasketBakery, pct: 30, color: "#00B98C" },
    { label: copy.vizBasketSnacks, pct: 20, color: "#008F6B" },
    { label: copy.vizBasketOther,  pct: 10, color: "#006A4F" },
  ];
  return (
    <div className="lp-viz lp-viz-basket">
      <div className="lp-viz-basket-bar">
        {segments.map((s) => (
          <span
            key={s.label}
            className="lp-viz-basket-seg"
            style={{ width: `${s.pct}%`, background: s.color }}
          />
        ))}
      </div>
      <div className="lp-viz-basket-legend">
        {segments.map((s) => (
          <span key={s.label}>{s.label}</span>
        ))}
      </div>
      <div className="lp-viz-cap">{copy.vizCapBasket}</div>
    </div>
  );
}

function buildDashboardSections(copy: LandingCopy): DashSection[] {
  return [
    {
      label: copy.dashShopperLabel,
      shortLabel: copy.dashShopperShort,
      accent: "#00D9A3",
      tiles: [
        { Icon: User,           title: copy.dashShopper1Title, desc: copy.dashShopper1Desc, Viz: VizCRMCounter  },
        { Icon: MapPin,         title: copy.dashShopper2Title, desc: copy.dashShopper2Desc, Viz: VizUKMap       },
        { Icon: Clock,          title: copy.dashShopper3Title, desc: copy.dashShopper3Desc, Viz: VizWeeklyBars  },
        { Icon: ShoppingBasket, title: copy.dashShopper4Title, desc: copy.dashShopper4Desc, Viz: VizBasketStack },
      ],
    },
    {
      label: copy.dashConvLabel,
      shortLabel: copy.dashConvShort,
      accent: "#FF5A8C",
      tiles: [
        { Icon: MessageSquare,     title: copy.dashConv1Title, desc: copy.dashConv1Desc, Viz: VizQuestionDonut    },
        { Icon: Smile,             title: copy.dashConv2Title, desc: copy.dashConv2Desc, Viz: VizSentimentBar     },
        { Icon: AlertTriangle,     title: copy.dashConv3Title, desc: copy.dashConv3Desc, Viz: VizConfusionBubbles },
        { Icon: MessageCircleMore, title: copy.dashConv4Title, desc: copy.dashConv4Desc, Viz: VizPurchaseFunnel   },
        { Icon: Languages,         title: copy.dashConv5Title, desc: copy.dashConv5Desc, Viz: VizLangPills        },
      ],
    },
    {
      label: copy.dashDistLabel,
      shortLabel: copy.dashDistShort,
      accent: "#5AA9FF",
      tiles: [
        { Icon: Store,        title: copy.dashDist1Title, desc: copy.dashDist1Desc, Viz: VizStoreAvailability },
        { Icon: AlertCircle,  title: copy.dashDist2Title, desc: copy.dashDist2Desc, Viz: VizOOSAlerts         },
        { Icon: Map,          title: copy.dashDist3Title, desc: copy.dashDist3Desc, Viz: VizHeatmap           },
        { Icon: Home,         title: copy.dashDist4Title, desc: copy.dashDist4Desc, Viz: VizHouseholdGrid     },
        { Icon: Grid3x3,      title: copy.dashDist5Title, desc: copy.dashDist5Desc, Viz: VizFasciaBars        },
      ],
    },
    {
      label: copy.dashCampLabel,
      shortLabel: copy.dashCampShort,
      accent: "#FFB84D",
      tiles: [
        { Icon: Target,     title: copy.dashCamp1Title, desc: copy.dashCamp1Desc, Viz: VizEngagementRate },
        { Icon: Gamepad2,   title: copy.dashCamp2Title, desc: copy.dashCamp2Desc, Viz: VizGameFunnel     },
        { Icon: Receipt,    title: copy.dashCamp3Title, desc: copy.dashCamp3Desc, Viz: VizRedemption     },
        { Icon: TrendingUp, title: copy.dashCamp4Title, desc: copy.dashCamp4Desc, Viz: VizROIDashboard   },
      ],
    },
    {
      label: copy.dashCompLabel,
      shortLabel: copy.dashCompShort,
      accent: "#B794F6",
      tiles: [
        { Icon: ShieldCheck,  title: copy.dashComp1Title, desc: copy.dashComp1Desc, Viz: VizComplianceScore },
        { Icon: FileText,     title: copy.dashComp2Title, desc: copy.dashComp2Desc, Viz: VizAuditLogs       },
        { Icon: AlertOctagon, title: copy.dashComp3Title, desc: copy.dashComp3Desc, Viz: VizViolationZero   },
        { Icon: BarChart2,    title: copy.dashComp4Title, desc: copy.dashComp4Desc, Viz: VizNutritionBars   },
      ],
    },
  ];
}

function PhoneScreen({ activeTab, copy }: { activeTab: number; copy: LandingCopy }) {
  return (
    <div key={activeTab} className="lp-exp-phone-view" data-view={activeTab}>
      {activeTab === 0 && <PhoneViewForm copy={copy} />}
      {activeTab === 1 && <PhoneViewChat copy={copy} />}
      {activeTab === 2 && <PhoneViewMap copy={copy} />}
      {activeTab === 3 && <PhoneViewWheel copy={copy} />}
      {activeTab === 4 && <PhoneViewNutrition copy={copy} />}
    </div>
  );
}

function PhoneViewForm({ copy }: { copy: LandingCopy }) {
  return (
    <div className="lp-pv-screen lp-pv-prize">
      <div className="lp-pv-prize-head">
        <span className="lp-pv-trophy" aria-hidden="true">
          <svg viewBox="0 0 12 12">
            <path d="M 3 2 L 9 2 L 9 5 C 9 6.5 7.5 8 6 8 C 4.5 8 3 6.5 3 5 Z" fill="#FFB84D" />
            <rect x="5" y="8" width="2" height="2" fill="#FFB84D" />
            <rect x="3.5" y="10" width="5" height="1" fill="#FFB84D" rx="0.5" />
          </svg>
        </span>
        <span className="lp-pv-prize-title">{copy.pvFormTitle}</span>
      </div>
      <div className="lp-pv-prize-sub">{copy.pvFormSub}</div>
      <div className="lp-pv-prize-form">
        <div className="lp-pv-prize-field" style={{ animationDelay: "0s" }}>
          <span className="lp-pv-prize-label">{copy.pvFormName}</span>
          <span className="lp-pv-prize-value">
            Ana R.
            <span className="lp-pv-prize-cursor" aria-hidden="true" />
          </span>
        </div>
        <div className="lp-pv-prize-field" style={{ animationDelay: "0.8s" }}>
          <span className="lp-pv-prize-label">{copy.pvFormEmail}</span>
          <span className="lp-pv-prize-value">a*****@gmail.com</span>
        </div>
        <div className="lp-pv-prize-field" style={{ animationDelay: "1.6s" }}>
          <span className="lp-pv-prize-label">{copy.pvFormPostcode}</span>
          <span className="lp-pv-prize-value">SE1</span>
          <span className="lp-pv-prize-tick" aria-hidden="true">
            <svg viewBox="0 0 10 10"><path d="M 2 5 L 4 7 L 8 3" stroke="#0A0A0D" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </span>
        </div>
      </div>
      <button type="button" className="lp-pv-cta-btn" aria-hidden="true">{copy.pvFormCta}</button>
      <div className="lp-pv-footnote">{copy.pvFormFootnote}</div>
    </div>
  );
}

function PhoneViewChat({ copy }: { copy: LandingCopy }) {
  return (
    <div className="lp-pv-screen lp-pv-chat">
      <div className="lp-pv-chat-header">
        <span className="lp-pv-ai-mark" aria-hidden="true">
          <svg viewBox="0 0 10 10">
            <path d="M 5 1 L 6 4 L 9 5 L 6 6 L 5 9 L 4 6 L 1 5 L 4 4 Z" fill="#00D9A3" />
          </svg>
        </span>
        <span className="lp-pv-chat-head-text">
          <span className="lp-pv-chat-title">{copy.pvChatTitle}</span>
          <span className="lp-pv-chat-sub">{copy.pvChatSub}</span>
        </span>
      </div>
      <div className="lp-pv-chat-thread">
        <span className="lp-pv-chat-bubble lp-pv-chat-bubble--user" style={{ animationDelay: "0s" }}>
          {copy.pvChatBubble1}
        </span>
        <span className="lp-pv-chat-bubble lp-pv-chat-bubble--ai" style={{ animationDelay: "1s" }}>
          {copy.pvChatBubble2}
        </span>
        <span className="lp-pv-chat-bubble lp-pv-chat-bubble--user" style={{ animationDelay: "2s" }}>
          {copy.pvChatBubble3}
        </span>
        <span className="lp-pv-chat-bubble lp-pv-chat-bubble--ai" style={{ animationDelay: "3s" }}>
          {copy.pvChatBubble4}
        </span>
      </div>
      <div className="lp-pv-chat-input" aria-hidden="true">
        <span className="lp-pv-chat-placeholder">{copy.pvChatPlaceholder}</span>
        <span className="lp-pv-chat-send">
          <svg viewBox="0 0 10 10"><path d="M 2 5 L 8 5 M 5 2 L 8 5 L 5 8" stroke="#0A0A0D" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </span>
      </div>
      <div className="lp-pv-footnote">{copy.pvChatFootnote}</div>
    </div>
  );
}

function PhoneViewMap({ copy }: { copy: LandingCopy }) {
  const pins = [
    { x: 22, y: 38, r: 2.5, fill: "#5AA9FF", alpha: 1,   label: "0.3mi",  closest: true,  out: false },
    { x: 62, y: 44, r: 2.5, fill: "#5AA9FF", alpha: 0.8, label: "0.8mi",  closest: false, out: false },
    { x: 20, y: 70, r: 2.5, fill: "#5AA9FF", alpha: 0.5, label: "1.2mi",  closest: false, out: false },
    { x: 66, y: 76, r: 2.5, fill: "#E11D48", alpha: 1,   label: "2.1mi",  closest: false, out: true  },
  ];
  return (
    <div className="lp-pv-screen lp-pv-map">
      <div className="lp-pv-map-title">{copy.pvMapTitle}</div>
      <div className="lp-pv-map-canvas">
        <svg viewBox="0 0 88 100" className="lp-pv-map-svg" aria-hidden="true" preserveAspectRatio="none">
          <g stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" fill="none">
            {Array.from({ length: 6 }).map((_, i) => (
              <line key={`h${i}`} x1="0" y1={15 + i * 14} x2="88" y2={15 + i * 14} />
            ))}
            {Array.from({ length: 5 }).map((_, i) => (
              <line key={`v${i}`} x1={10 + i * 16} y1="0" x2={10 + i * 16} y2="100" />
            ))}
          </g>
          {pins.map((p, i) => (
            <g key={i}>
              {!p.out && (
                <circle
                  cx={p.x} cy={p.y} r="3"
                  fill="none" stroke={p.fill} strokeWidth="1"
                  className="lp-pv-map-ring"
                  style={{ animationDelay: `${i * 0.5}s` }}
                  opacity={p.alpha}
                />
              )}
              <circle cx={p.x} cy={p.y} r={p.r} fill={p.fill} opacity={p.alpha} className={p.out ? "lp-pv-map-alert" : ""} />
              <text x={p.x + 5} y={p.y + 1} fontSize="4.5" fill="rgba(255,255,255,0.8)" fontWeight="600">{p.label}</text>
              {p.out && <text x={p.x + 5} y={p.y + 5.5} fontSize="3.5" fill="#E11D48" fontWeight="600">{copy.pvMapOos}</text>}
            </g>
          ))}
        </svg>
      </div>
      <div className="lp-pv-map-card">
        <div className="lp-pv-map-card-row">
          <span className="lp-pv-map-dot" />
          <span className="lp-pv-map-label">{copy.pvMapPin}</span>
        </div>
        <button type="button" className="lp-pv-cta-btn lp-pv-cta-btn--blue" aria-hidden="true">{copy.pvMapCta}</button>
      </div>
      <div className="lp-pv-footnote">{copy.pvMapFootnote}</div>
    </div>
  );
}

function PhoneViewWheel({ copy }: { copy: LandingCopy }) {
  const wedges = [
    { label: "50pts",            color: "#00D9A3", emphasis: false },
    { label: "100",              color: "#7C5CFF", emphasis: false },
    { label: copy.pvWheelWedgeWin, color: "#F5A524", emphasis: true  },
    { label: copy.pvWheelWedgeTry, color: "#E11D48", emphasis: false },
    { label: "200",              color: "#2563EB", emphasis: false },
    { label: "500",              color: "#00A86B", emphasis: false },
  ];
  const R = 32;
  return (
    <div className="lp-pv-screen lp-pv-wheel-view">
      <div className="lp-pv-wheel-head">
        <span className="lp-pv-wheel-title">{copy.pvWheelTitle}</span>
        <span className="lp-pv-credits">{copy.pvWheelCredits}</span>
      </div>
      <div className="lp-pv-wheel-wrap">
        <div className="lp-pv-wheel-stage">
          <svg viewBox="0 0 70 76" className="lp-pv-wheel-svg" aria-hidden="true">
            {/* Static wrapper positions centre of wheel at (35, 41) */}
            <g transform="translate(35 41)">
              {/* Inner group rotates around its own centre (0,0 in local coords) */}
              <g className="lp-pv-wheel-disc">
                {wedges.map((w, i) => {
                  const a0 = i * 60 - 90;
                  const a1 = (i + 1) * 60 - 90;
                  const toXY = (d: number, r: number): [number, number] => [
                    r * Math.cos((d * Math.PI) / 180),
                    r * Math.sin((d * Math.PI) / 180),
                  ];
                  const [x0, y0] = toXY(a0, R);
                  const [x1, y1] = toXY(a1, R);
                  const tmid = (a0 + a1) / 2;
                  const [tx, ty] = toXY(tmid, 18);
                  return (
                    <g key={w.label}>
                      <path
                        d={`M 0 0 L ${x0} ${y0} A ${R} ${R} 0 0 1 ${x1} ${y1} Z`}
                        fill={w.color}
                        opacity={w.emphasis ? 1 : 0.92}
                        stroke="rgba(255, 255, 255, 0.2)"
                        strokeWidth="0.4"
                      />
                      <g transform={`translate(${tx} ${ty}) rotate(${tmid + 90})`}>
                        <text
                          x="0"
                          y="0"
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontSize={w.emphasis ? "6" : "5"}
                          fontWeight="800"
                          fill="#ffffff"
                          style={{ filter: "drop-shadow(0 0.3px 0.5px rgba(0, 0, 0, 0.7))" }}
                        >
                          {w.label}
                        </text>
                      </g>
                    </g>
                  );
                })}
                {/* centre hub */}
                <circle r="4" fill="#0A0A0D" stroke="#FFB84D" strokeWidth="0.8" />
              </g>
            </g>
            {/* Pointer at 12 o'clock — outside rotating group so it stays still */}
            <path d="M 35 3 L 39 9 L 31 9 Z" fill="#ffffff" stroke="#FFB84D" strokeWidth="0.5" />
          </svg>
        </div>
        <div className="lp-pv-coins" aria-hidden="true">
          <span className="lp-pv-coin" style={{ left: "20%", animationDelay: "0s"   }} />
          <span className="lp-pv-coin" style={{ left: "50%", animationDelay: "0.7s" }} />
          <span className="lp-pv-coin" style={{ left: "75%", animationDelay: "1.4s" }} />
        </div>
      </div>
      <button type="button" className="lp-pv-cta-btn lp-pv-cta-btn--gold" aria-hidden="true">{copy.pvWheelCta}</button>
      <div className="lp-pv-footnote">{copy.pvWheelFootnote}</div>
    </div>
  );
}

function PhoneViewNutrition({ copy }: { copy: LandingCopy }) {
  const slices = [
    { label: copy.vizNutFat,    pct: 8,  color: "#00A86B" },
    { label: copy.vizNutSatFat, pct: 12, color: "#FFB84D" },
    { label: copy.vizNutSugar,  pct: 28, color: "#E11D48" },
    { label: copy.vizNutSalt,   pct: 4,  color: "#00A86B" },
  ];
  const R = 18;
  const cx = 44, cy = 38;
  return (
    <div className="lp-pv-screen lp-pv-nut">
      <div className="lp-pv-nut-title">{copy.pvNutTitle}</div>
      <div className="lp-pv-nut-toggles" aria-hidden="true">
        <span className="lp-pv-nut-toggle" data-active="true">{copy.pvNutPer100g}</span>
        <span className="lp-pv-nut-toggle">{copy.pvNutPerServing}</span>
      </div>
      <div className="lp-pv-nut-wrap">
        <svg viewBox="0 0 88 76" className="lp-pv-nut-svg" aria-hidden="true">
          <g transform={`translate(${cx} ${cy})`}>
            <circle r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.8" />
            {slices.map((s, i) => {
              const a0 = (i * 360) / slices.length - 90;
              const a1 = ((i + 1) * 360) / slices.length - 90;
              const toXY = (d: number, r: number): [number, number] => [r * Math.cos((d * Math.PI) / 180), r * Math.sin((d * Math.PI) / 180)];
              const [x0, y0] = toXY(a0, R);
              const [x1, y1] = toXY(a1, R);
              return (
                <path
                  key={s.label}
                  d={`M 0 0 L ${x0} ${y0} A ${R} ${R} 0 0 1 ${x1} ${y1} Z`}
                  fill={s.color}
                  className="lp-pv-nut-slice"
                  style={{ animationDelay: `${i * 0.3}s` }}
                />
              );
            })}
            <circle r="9" fill="#0A0A0D" stroke="rgba(255,255,255,0.15)" strokeWidth="0.8" />
            <text y="-0.5" textAnchor="middle" fontSize="6" fontWeight="800" fill="#ffffff">380</text>
            <text y="4" textAnchor="middle" fontSize="3.5" fill="rgba(255,255,255,0.7)">kcal</text>
          </g>
        </svg>
      </div>
      <div className="lp-pv-nut-legend">
        {slices.map((s) => (
          <span key={s.label}>
            <i style={{ background: s.color }} />
            <strong>{s.label}</strong>
            <b>{s.pct}%</b>
          </span>
        ))}
      </div>
      <button type="button" className="lp-pv-cta-btn" aria-hidden="true">{copy.pvNutCta}</button>
      <div className="lp-pv-footnote">{copy.pvNutFootnote}</div>
    </div>
  );
}

function DashboardCycle({ copy, onActiveChange }: { copy: LandingCopy; onActiveChange?: (i: number) => void }) {
  const sections = useMemo(() => buildDashboardSections(copy), [copy]);
  const [active, setActive] = useState(0);
  const [cycleStart, setCycleStart] = useState<number>(() => performance.now());
  const [progress, setProgress] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    onActiveChange?.(active);
  }, [active, onActiveChange]);

  const lockedRef = useRef(false);
  useEffect(() => {
    (window as any).__dashFreeze = () => {
      lockedRef.current = true;
      pausedRef.current = true;
      if (pauseTimerRef.current) window.clearTimeout(pauseTimerRef.current);
    };
    (window as any).__dashUnfreeze = () => {
      lockedRef.current = false;
      pausedRef.current = false;
    };
    return () => {
      delete (window as any).__dashFreeze;
      delete (window as any).__dashUnfreeze;
    };
  }, []);

  const pausedRef = useRef(false);
  const pauseTimerRef = useRef<number | null>(null);
  const tablistRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    let raf = 0;
    const tick = (now: number) => {
      if (!pausedRef.current && !lockedRef.current) {
        const elapsed = now - cycleStart;
        const p = Math.min(1, elapsed / 4000);
        setProgress(p);
        if (p >= 1) {
          setActive((a) => (a + 1) % sections.length);
          setCycleStart(now);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [cycleStart, sections.length]);

  const selectTab = (i: number, pauseMs: number) => {
    setActive(i);
    setCycleStart(performance.now());
    setProgress(0);
    pausedRef.current = true;
    if (pauseTimerRef.current) window.clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = window.setTimeout(() => {
      pausedRef.current = false;
      setCycleStart(performance.now());
    }, pauseMs);
  };

  const handleMouseEnter = () => {
    pausedRef.current = true;
    if (pauseTimerRef.current) window.clearTimeout(pauseTimerRef.current);
  };
  const handleMouseLeave = () => {
    if (pauseTimerRef.current) window.clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = window.setTimeout(() => {
      pausedRef.current = false;
      setCycleStart(performance.now());
    }, 2000);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      selectTab((active + 1) % sections.length, 8000);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      selectTab((active - 1 + sections.length) % sections.length, 8000);
    }
  };

  const section = sections[active];
  return (
    <div
      className="lp-dash lp-dash-cycle"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        ref={tablistRef}
        className="lp-dash-tabs"
        role="tablist"
        aria-label="Dashboard intelligence sections"
        onKeyDown={handleKey}
      >
        {sections.map((s, i) => (
          <button
            key={s.label}
            id={`lp-dash-tab-${i}`}
            role="tab"
            type="button"
            aria-selected={i === active}
            aria-controls={`lp-dash-panel-${i}`}
            tabIndex={i === active ? 0 : -1}
            className="lp-dash-tab"
            data-active={i === active}
            onClick={() => selectTab(i, 8000)}
          >
            <span className="lp-dash-tab-label">{s.shortLabel}</span>
            {i === active && (
              <span
                className="lp-dash-tab-progress"
                aria-hidden="true"
                style={{ ["--fill" as string]: `${progress * 100}%` }}
              />
            )}
          </button>
        ))}
      </div>
      <section
        key={active}
        id={`lp-dash-panel-${active}`}
        className="lp-dash-section"
        role="tabpanel"
        aria-labelledby={`lp-dash-tab-${active}`}
        style={{ ["--tile-accent" as string]: section.accent }}
      >
        <h4 className="lp-dash-section-label">{section.label}</h4>
        <div className="lp-dash-tiles">
          {section.tiles.map((t) => (
            <div key={t.title} className="lp-dash-tile">
              <div className="lp-dash-tile-head">
                <span className="lp-dash-tile-icon" aria-hidden="true">
                  <t.Icon size={18} strokeWidth={1.75} />
                </span>
                <h5 className="lp-dash-tile-title">{t.title}</h5>
              </div>
              <p className="lp-dash-tile-desc">{t.desc}</p>
              {t.Viz && (
                <div className="lp-dash-tile-viz">
                  <t.Viz copy={copy} />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function StepIconEnter() {
  return (
    <svg viewBox="0 0 80 80" className="lp-step-icon-svg" aria-hidden="true">
      <g transform="translate(40 40)">
        <circle r="30" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" className="lp-step-icon-ring" />
        {/* Phone body */}
        <rect x="-14" y="-22" width="28" height="44" rx="5" fill="none" stroke="#ffffff" strokeWidth="1.75" />
        <rect x="-11" y="-18" width="22" height="32" rx="2" fill="rgba(255,255,255,0.08)" />
        {/* Scan reticle corners */}
        <path d="M -7 -12 L -7 -14 L -5 -14" stroke="#00F5B3" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d="M 5 -14 L 7 -14 L 7 -12" stroke="#00F5B3" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d="M 7 8 L 7 10 L 5 10" stroke="#00F5B3" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d="M -5 10 L -7 10 L -7 8" stroke="#00F5B3" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        {/* Home indicator */}
        <rect x="-4" y="17" width="8" height="1.5" rx="0.75" fill="rgba(255,255,255,0.6)" />
      </g>
    </svg>
  );
}

function StepIconEngage() {
  // Five dots in a pentagon with central connector, sparkle above
  const radius = 18;
  const dots = Array.from({ length: 5 }).map((_, i) => {
    const a = (i * 72 - 90) * (Math.PI / 180);
    return { x: 40 + radius * Math.cos(a), y: 42 + radius * Math.sin(a), i };
  });
  return (
    <svg viewBox="0 0 80 80" className="lp-step-icon-svg" aria-hidden="true">
      {/* Sparkle above */}
      <g transform="translate(40 18)">
        <path d="M 0 -7 L 1.5 -1.5 L 7 0 L 1.5 1.5 L 0 7 L -1.5 1.5 L -7 0 L -1.5 -1.5 Z" fill="#ffffff" className="lp-step-sparkle" />
      </g>
      {/* Connector lines from centre */}
      {dots.map((d, i) => (
        <line key={`ln${i}`} x1="40" y1="42" x2={d.x} y2={d.y} stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" />
      ))}
      {/* Central node */}
      <circle cx="40" cy="42" r="3" fill="#ffffff" />
      {/* Pentagon dots */}
      {dots.map((d) => (
        <circle
          key={d.i}
          cx={d.x} cy={d.y} r="4"
          fill="#ffffff"
          className="lp-step-pentagon-dot"
          style={{ animationDelay: `${d.i * 0.2}s` }}
        />
      ))}
    </svg>
  );
}

function StepIconLearn() {
  return (
    <svg viewBox="0 0 80 80" className="lp-step-icon-svg" aria-hidden="true">
      {/* Three horizontal bars */}
      <rect x="14" y="42" width="30" height="6" rx="2" fill="rgba(255,255,255,0.65)" />
      <rect x="14" y="52" width="22" height="6" rx="2" fill="rgba(255,255,255,0.8)" />
      <rect x="14" y="62" width="36" height="6" rx="2" fill="#ffffff" />
      {/* Trend line rising */}
      <path
        d="M 14 40 L 24 30 L 34 34 L 44 22 L 56 16"
        stroke="#ffffff"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="56" cy="16" r="2.5" fill="#ffffff" />
      <path d="M 52 12 L 60 12 L 60 20" stroke="#ffffff" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {/* Rising data particles */}
      <circle cx="28" cy="28" r="1.2" fill="#ffffff" className="lp-step-particle" style={{ animationDelay: "0s" }} />
      <circle cx="38" cy="20" r="1" fill="#ffffff" className="lp-step-particle" style={{ animationDelay: "0.7s" }} />
      <circle cx="48" cy="14" r="1.3" fill="#ffffff" className="lp-step-particle" style={{ animationDelay: "1.4s" }} />
    </svg>
  );
}

function ThreeStepWorkflow({ copy }: { copy: LandingCopy }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const circleRefs = useRef<(HTMLDivElement | null)[]>([null, null, null]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }),
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* JS-driven animations (bypasses CSS throttling under reduced-motion).
     Drives: circle breathing, icon pulse synced to horizontal-stream journey,
     and the horizontal binary stream character positions. */
  useEffect(() => {
    const start = performance.now();
    const hstreamCycle = 6000;
    const breatheMs = 4000;
    const breatheDelays = [0, 1300, 2600];
    const iconOffsets = [1000, 3000, 5000];      // when stream passes each circle
    const charCount = 18;

    const id = window.setInterval(() => {
      const now = performance.now();

      // Horizontal stream — chars spread across 0..100%, advancing continuously.
      const hStream = document.querySelectorAll<HTMLElement>(".lp-steps-hstream-char");
      const pool = ["0", "1", "0", "1", "0", "1", "A", "3", "7", "F", "2"];
      hStream.forEach((c, idx) => {
        const offset = (idx / charCount);
        const phase = ((now - start) / hstreamCycle + offset) % 1;
        c.style.left = `${phase * 104 - 2}%`;
        // Opacity envelope: fade in first 8%, fade out last 8%
        const op = phase < 0.08
          ? phase / 0.08
          : phase > 0.92 ? (1 - phase) / 0.08 : 1;
        c.style.opacity = String(Math.max(0, Math.min(1, op)));
        // Colour graduates: teal → orange → violet as chars travel
        const col = phase < 0.33 ? "#00D9A3"
                  : phase < 0.66 ? "#E8542B"
                  : "#7C5CFF";
        c.style.color = col;
        // Occasionally refresh character to add flicker
        if (Math.random() < 0.03) {
          c.textContent = pool[Math.floor(Math.random() * pool.length)];
        }
      });

      // Circle breathing — cosine curve 1.0..1.02..1.0
      circleRefs.current.forEach((c, i) => {
        if (!c) return;
        const bt = ((now - start - breatheDelays[i]) % breatheMs + breatheMs) % breatheMs;
        const s = 1 + 0.01 * (1 - Math.cos((bt / breatheMs) * 2 * Math.PI));
        c.style.transform = `scale(${s})`;
        // Icon pulse at moment stream passes behind each circle
        const iconEl = c.querySelector<HTMLElement>(".lp-step-icon-svg");
        const it = ((now - start - iconOffsets[i]) % hstreamCycle + hstreamCycle) % hstreamCycle;
        const pulseWindow = 500;
        let iconScale = 1;
        let iconShadow = "0 0 0 rgba(255,255,255,0)";
        if (it < pulseWindow) {
          const p = it / pulseWindow;
          const k = Math.sin(p * Math.PI);
          iconScale = 1 + 0.08 * k;
          iconShadow = `0 0 ${6 * k}px rgba(255,255,255,${0.6 * k})`;
        }
        if (iconEl) {
          iconEl.style.transform = `scale(${iconScale})`;
          iconEl.style.filter = `drop-shadow(${iconShadow})`;
        }
      });
    }, 50);
    return () => window.clearInterval(id);
  }, []);

  const steps = [
    {
      num: "01",
      label: "ENTER",
      title: copy.step1Title,
      href: "#platform",
      desc: copy.step1Desc,
      Composition: StepIconEnter,
      variant: "enter",
      // Mobile label row — shown only below 768px (CSS-gated); replaces the satellite chips.
      pillRow: copy.step1PillRow,
      // Clock positions: 12, 2, 4, 6, 8, 10 o'clock
      chips: [
        { label: copy.chipWebsite, Icon: Globe,     angle:   0 },
        { label: copy.chipSocial,  Icon: Instagram, angle:  60 },
        { label: copy.chipEmail,   Icon: Mail,      angle: 120 },
        { label: copy.chipQr,      Icon: QrCode,    angle: 180 },
        { label: copy.chipOoh,     Icon: Tv,        angle: 240 },
        { label: copy.chipInStore, Icon: Store,     angle: 300 },
      ],
    },
    {
      num: "02",
      label: "ENGAGE",
      title: copy.step2Title,
      href: "#modules",
      desc: copy.step2Desc,
      Composition: StepIconEngage,
      variant: "engage",
      pillRow: copy.step2PillRow,
      // Clock positions: 12, 2, 4, 8, 10 — avoiding 3 and 9 o'clock (stream path)
      chips: [
        { label: copy.chipLearn, Icon: GraduationCap, angle:   0 },
        { label: copy.chipPlay,  Icon: Gamepad2,      angle:  60 },
        { label: copy.chipSay,   Icon: MessageCircle, angle: 120 },
        { label: copy.chipWin,   Icon: Trophy,        angle: 240 },
        { label: copy.chipEarn,  Icon: Coins,         angle: 300 },
      ],
    },
    {
      num: "03",
      label: "LEARN",
      title: copy.step3Title,
      href: "#how-it-works",
      desc: copy.step3Desc,
      Composition: StepIconLearn,
      variant: "learn",
      pillRow: copy.step3PillRow,
      chips: [
        { label: copy.chipShoppers,     Icon: User,         angle:   0 },
        { label: copy.chipConversation, Icon: MessageSquare, angle:  60 },
        { label: copy.chipLocation,     Icon: MapPin,        angle: 120 },
        { label: copy.chipEngagement,   Icon: TrendingUp,    angle: 240 },
        { label: copy.chipCompliance,   Icon: ShieldCheck,   angle: 300 },
      ],
    },
  ];

  return (
    <section className="lp-steps" aria-labelledby="lp-steps-title">
      <div className="lp-steps-inner" ref={ref} data-visible={visible}>
        <div className="lp-steps-head">
          <h2 id="lp-steps-title" className="lp-steps-title">{copy.stepsTitle}</h2>
          <p className="lp-steps-sub">{copy.stepsSub}</p>
        </div>

        <div className="lp-steps-row">
          <div className="lp-steps-line" aria-hidden="true">
            <div className="lp-steps-hstream">
              {Array.from({ length: 18 }).map((_, i) => {
                const pool = ["0", "1", "0", "1", "0", "1", "A", "3", "7", "F"];
                const ch = pool[i % pool.length];
                return (
                  <span
                    key={i}
                    className="lp-steps-hstream-char"
                    style={{ ["--i" as string]: i }}
                  >
                    {ch}
                  </span>
                );
              })}
            </div>
          </div>

          {steps.map((s, i) => (
            <a
              key={s.num}
              href={s.href}
              className="lp-step"
              data-variant={s.variant}
              style={{ ["--i" as string]: i }}
            >
              <div
                className="lp-step-circle"
                aria-hidden="true"
                ref={(el) => { circleRefs.current[i] = el; }}
              >
                <span className="lp-step-specular" />
                <span className="lp-step-ring" />
                <s.Composition />
                {/* Layer 5 — ambient light particles (confined near circle) */}
                <div className="lp-step-particles">
                  {[0, 1, 2].map((p) => (
                    <span
                      key={p}
                      className="lp-step-particle-orb"
                      style={{
                        ["--i" as string]: p * 2,
                        ["--r" as string]: `${95 + (p * 5)}px`,
                        ["--s" as string]: `${18 + (p * 4)}s`,
                        ["--o" as string]: `${5 + p}s`,
                      }}
                    />
                  ))}
                </div>
              </div>
              {/* Satellite chips orbiting each circle at fixed clock positions.
                  Each chip has a thin binary-character stream flowing inward. */}
              <div className="lp-step-sats" aria-label={`${s.title} channels`}>
                {s.chips.map((c) => (
                  <div
                    key={c.label}
                    className="lp-step-sat-slot"
                    style={{ ["--a" as string]: `${c.angle}deg` }}
                  >
                    <span className="lp-step-stream" aria-hidden="true">
                      <span className="lp-step-stream-char" style={{ ["--i" as string]: 0 }}>0</span>
                      <span className="lp-step-stream-char" style={{ ["--i" as string]: 1 }}>1</span>
                      <span className="lp-step-stream-char" style={{ ["--i" as string]: 2 }}>A</span>
                    </span>
                    <div
                      role="button"
                      tabIndex={-1}
                      className="lp-step-sat-chip"
                      aria-label={`${s.label === "ENTER" ? copy.chipAriaEntry : s.label === "ENGAGE" ? copy.chipAriaModule : copy.chipAriaIntel}: ${c.label}`}
                    >
                      <span className="lp-step-sat-icon" aria-hidden="true">
                        <c.Icon size={18} strokeWidth={1.75} />
                      </span>
                      <span className="lp-step-sat-label">{c.label}</span>
                    </div>
                  </div>
                ))}
              </div>
              <h3 className="lp-step-title">{s.title}</h3>
              {/* Mobile-only label row — hidden on desktop via CSS. */}
              <div className="lp-step-pills" aria-hidden="true">{s.pillRow}</div>
              <p className="lp-step-desc">{s.desc}</p>
              {/* Mobile-only downward chevron between stacked circles (hidden on last + on desktop). */}
              {i < steps.length - 1 && (
                <span className="lp-step-mobile-chevron" aria-hidden="true">
                  <ChevronDown size={20} />
                </span>
              )}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function IntegrationsStrip({ copy }: { copy: LandingCopy }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Brand names stay as-is (proper nouns / logos); only the captions translate.
  const tiles = [
    { name: "ShopAi",          caption: copy.intgCap1, variant: "shopai"        },
    { name: "aws",             caption: copy.intgCap2, variant: "aws"           },
    { name: "ANTHROP\\C",      caption: copy.intgCap3, variant: "anthropic"     },
    { name: "OpenAI",          caption: copy.intgCap4, variant: "openai"        },
    { name: "Google",          caption: copy.intgCap5, variant: "google"        },
    { name: "Open Food Facts", caption: copy.intgCap6, variant: "openfoodfacts" },
    { name: "stripe",          caption: copy.intgCap7, variant: "stripe"        },
  ];

  return (
    <section className="lp-integrations" aria-labelledby="lp-integrations-title">
      <div className="lp-integrations-inner" ref={ref} data-visible={visible}>
        <div className="lp-integrations-head">
          <h2 id="lp-integrations-title" className="lp-integrations-title">{copy.intgTitle}</h2>
          <p className="lp-integrations-sub">{copy.intgSub}</p>
        </div>

        <ul className="lp-integrations-grid" role="list">
          {tiles.map((t, i) => (
            <li
              key={t.name}
              className="lp-integration-tile"
              data-variant={t.variant}
              style={{ ["--i" as string]: i }}
            >
              <span className="lp-integration-logo">{t.name}</span>
              <span className="lp-integration-caption">{t.caption}</span>
            </li>
          ))}
        </ul>

        <p className="lp-integrations-footnote">{copy.intgFootnote}</p>
      </div>
    </section>
  );
}

function CaptureGrid({ copy }: { copy: LandingCopy }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const chips = [
    { Icon: Mail,            label: copy.capEmail   },
    { Icon: User,            label: copy.capName    },
    { Icon: MapPin,          label: copy.capAddress },
    { Icon: Globe,           label: copy.capGps     },
    { Icon: Package,         label: copy.capProduct },
    { Icon: ShoppingBasket,  label: copy.capBasket  },
    { Icon: PoundSterling,   label: copy.capSpend   },
    { Icon: MessageSquare,   label: copy.capChat    },
  ];

  return (
    <div
      ref={ref}
      className="lp-capture-grid"
      data-visible={visible}
    >
      {chips.map((c, i) => (
        <div
          key={c.label}
          className="lp-capture-chip"
          style={{ ["--i" as string]: i }}
        >
          <span className="lp-capture-icon" aria-hidden="true">
            <c.Icon size={14} strokeWidth={2} />
          </span>
          <span className="lp-capture-label">{c.label}</span>
        </div>
      ))}
    </div>
  );
}

function ColHeader({ num, label, active }: { num: string; label: string; active?: boolean }) {
  return (
    <div className="lp-journey-col-head">
      <span
        className="lp-journey-col-num"
        aria-hidden="true"
        data-active={active ? "true" : "false"}
      >
        {num}
      </span>
      <h3 className="lp-journey-col-title">{label}</h3>
    </div>
  );
}

function ChevronArrow() {
  return (
    <span className="lp-journey-chevron" aria-hidden="true">
      <svg viewBox="0 0 12 24" width="12" height="24">
        <path d="M 2 4 L 10 12 L 2 20" stroke="#00C9A7" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
      </svg>
    </span>
  );
}

function FullJourney({ copy }: { copy: LandingCopy }) {
  const [activeTab, setActiveTab] = useState(0);
  const [flowStop, setFlowStop] = useState(0);        // 0 | 1 | 2 when holding; -1 when transitioning
  const [flowLeftPct, setFlowLeftPct] = useState(11);

  useEffect(() => {
    const positions = [11, 41, 80];
    const start = performance.now();

    const compute = () => {
      const now = performance.now();
      const elapsed = (now - start) % 9000;
      let stop = -1;
      let left = positions[0];
      if (elapsed < 2000) {
        stop = 0;
        left = positions[0];
      } else if (elapsed < 3000) {
        const t = (elapsed - 2000) / 1000;
        left = positions[0] + (positions[1] - positions[0]) * t;
      } else if (elapsed < 5000) {
        stop = 1;
        left = positions[1];
      } else if (elapsed < 6000) {
        const t = (elapsed - 5000) / 1000;
        left = positions[1] + (positions[2] - positions[1]) * t;
      } else if (elapsed < 8000) {
        stop = 2;
        left = positions[2];
      } else {
        const t = (elapsed - 8000) / 1000;
        left = positions[2] + (positions[0] - positions[2]) * t;
      }
      setFlowStop((prev) => (prev === stop ? prev : stop));
      setFlowLeftPct(left);
    };

    compute();
    const id = window.setInterval(compute, 120);
    return () => window.clearInterval(id);
  }, []);

  return (
    <section
      className="lp-journey"
      id="how-it-works"
      aria-label="TalkPack end-to-end platform architecture"
    >
      <div className="lp-journey-inner">
        <div className="lp-journey-head">
          <h2 className="lp-journey-title">{copy.fullJourneyHeading}</h2>
          <p className="lp-journey-sub">{copy.fullJourneySub}</p>
        </div>

        <div className="lp-journey-shell">
          <div className="lp-journey-grid">
            <div className="lp-journey-col lp-journey-col-entry">
              <ColHeader num="01" label={copy.jColEntry} active={flowStop === 0} />

              <article
                className="lp-entry-card lp-entry-card--digital"
                style={{ ["--accent-bar" as string]: "#00C9A7" }}
              >
                <div className="lp-entry-card-head">
                  <span className="lp-entry-card-icon" aria-hidden="true">
                    <Globe size={32} strokeWidth={1.75} />
                  </span>
                  <h4 className="lp-entry-card-title">{copy.jEntryDigitalTitle}</h4>
                </div>
                <p className="lp-entry-card-desc">{copy.jEntryDigitalDesc}</p>
              </article>

              <article
                className="lp-entry-card lp-entry-card--social"
                style={{ ["--accent-bar" as string]: "#7C5CFF" }}
              >
                <div className="lp-entry-card-head">
                  <span className="lp-entry-card-icon" aria-hidden="true">
                    <Share2 size={32} strokeWidth={1.75} />
                  </span>
                  <h4 className="lp-entry-card-title">{copy.jEntrySocialTitle}</h4>
                </div>
                <p className="lp-entry-card-desc">{copy.jEntrySocialDesc}</p>
              </article>

              <article
                className="lp-entry-card lp-entry-card--physical"
                style={{ ["--accent-bar" as string]: "#2563EB" }}
              >
                <div className="lp-entry-card-head">
                  <span className="lp-entry-card-icon" aria-hidden="true">
                    <QrCode size={32} strokeWidth={1.75} />
                  </span>
                  <h4 className="lp-entry-card-title">{copy.jEntryPhysicalTitle}</h4>
                </div>
                <p className="lp-entry-card-desc">{copy.jEntryPhysicalDesc}</p>
                <div className="lp-entry-card-pills">
                  <span className="lp-entry-card-pill">{copy.jEntryPhysPill1}</span>
                  <span className="lp-entry-card-pill">{copy.jEntryPhysPill2}</span>
                  <span className="lp-entry-card-pill">{copy.jEntryPhysPill3}</span>
                </div>
              </article>
            </div>

            <ChevronArrow />

            <div className="lp-journey-col lp-journey-col-exp">
              <div className="lp-exp-top">
                <div className="lp-exp-top-left">
                  <ColHeader num="02" label={copy.jColExperience} active={flowStop === 1} />
                  <div className="lp-exp-step lp-exp-verify">
                    <div className="lp-exp-step-label">{copy.jExpVerifyLabel}</div>
                    <p className="lp-exp-step-text">{copy.jExpVerifyText}</p>
                  </div>
                </div>
                <div className="lp-exp-top-right">
                  <div className="lp-exp-phone" aria-hidden="true">
                    <span className="lp-exp-phone-speaker" />
                    <div className="lp-exp-phone-screen">
                      <PhoneScreen activeTab={activeTab} copy={copy} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="lp-exp-steps">
              <div className="lp-exp-step">
                <div className="lp-exp-step-label">{copy.jExpLearnLabel}</div>
                <div className="lp-exp-card" data-variant="learn">
                  <span className="lp-exp-pill">Pic-n-Learn</span>
                  <p className="lp-exp-card-body">{copy.jExpLearnBody}</p>
                </div>
              </div>

              <div className="lp-exp-step">
                <div className="lp-exp-step-label">{copy.jExpEngageLabel}</div>
                <div className="lp-exp-triple">
                  <div className="lp-exp-card" data-variant="play">
                    <span className="lp-exp-pill">Pic-n-Play</span>
                    <p className="lp-exp-card-body">{copy.jExpEngagePlayBody}</p>
                  </div>
                  <div className="lp-exp-card" data-variant="earn">
                    <span className="lp-exp-pill">Pic-n-Earn</span>
                    <p className="lp-exp-card-body">{copy.jExpEngageEarnBody}</p>
                  </div>
                  <div className="lp-exp-card" data-variant="say">
                    <span className="lp-exp-pill">Pic-n-Say</span>
                    <p className="lp-exp-card-body">{copy.jExpEngageSayBody}</p>
                  </div>
                </div>
              </div>

              <div className="lp-exp-step">
                <div className="lp-exp-step-label">{copy.jExpRewardLabel}</div>
                <div className="lp-exp-card" data-variant="win">
                  <span className="lp-exp-pill">Pic-n-Win</span>
                  <p className="lp-exp-card-body">{copy.jExpRewardBody}</p>
                </div>
              </div>

              <div className="lp-exp-step">
                <div className="lp-exp-step-label">{copy.jExpCaptureLabel}</div>
                <p className="lp-capture-sub">{copy.jExpCaptureSub}</p>
                <CaptureGrid copy={copy} />
              </div>
            </div>
          </div>

            <ChevronArrow />

            <div className="lp-journey-col lp-journey-col-dash">
              <ColHeader num="03" label={copy.jColDashboard} active={flowStop === 2} />
              <DashboardCycle copy={copy} onActiveChange={setActiveTab} />
            </div>
          </div>

          <div className="lp-journey-flow" aria-hidden="true">
            <div className="lp-journey-flow-line">
              <span
                className="lp-journey-flow-dot"
                style={{ left: `${flowLeftPct}%` }}
              />
            </div>
            <div className="lp-journey-flow-labels">
              <span data-active={flowStop === 0 ? "true" : "false"}>Shopper enters</span>
              <span aria-hidden="true" />
              <span data-active={flowStop === 1 ? "true" : "false"}>Shopper engages</span>
              <span aria-hidden="true" />
              <span data-active={flowStop === 2 ? "true" : "false"}>Platform learns</span>
            </div>
          </div>
        </div>

        <p className="lp-journey-caption">{copy.trustRight}</p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Footer (§6.14)
// ─────────────────────────────────────────────────────────────────────────────

function Footer({ copy, lang, onInfoOpen, onAdminClick }: { copy: LandingCopy; lang: LandingLanguage; onInfoOpen: (entry: InfoEntry) => void; onAdminClick: () => void }) {
  return (
    <footer className="lp-footer">
      <div className="lp-footer-top">
        {/*
          Platform + Modules columns: each link is a button that opens the
          InfoModal with the matching paragraph. Labels and modal titles come
          from landing-info-content.ts so they can't drift apart.
        */}
        <div className="lp-footer-col">
          <div className="lp-footer-col-head">{copy.footerColPlatform}</div>
          <ul>
            {PLATFORM_ORDER.map(key => {
              const entry = getPlatformInfo(key, lang);
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="lp-footer-link-btn"
                    onClick={() => onInfoOpen(entry)}
                    data-testid={`footer-platform-${key}`}
                  >
                    {entry.title}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="lp-footer-col">
          <div className="lp-footer-col-head">{copy.footerColModules}</div>
          <ul>
            {MODULE_ORDER.map(key => {
              const entry = getModuleInfo(key, lang);
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="lp-footer-link-btn"
                    onClick={() => onInfoOpen(entry)}
                    data-testid={`footer-module-${key}`}
                  >
                    {entry.title}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        {/*
          Company column. About + Contact only — Careers / Press were removed
          (the parent shopai.website covers those). About points at the parent
          site root; Contact uses mailto so the link works without depending
          on whatever the SPA at shopai.website renders for /contact.
        */}
        <div className="lp-footer-col">
          <div className="lp-footer-col-head">{copy.footerColCompany}</div>
          <ul>
            <li>
              <a
                href="https://www.shopai.website/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-company-about"
              >
                {copy.footerCompany1}
              </a>
            </li>
            <li>
              <a href="mailto:info@shopai.uk" data-testid="footer-company-contact">
                {copy.footerCompany2}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="lp-footer-mid">
        <div className="lp-footer-news-heading">{copy.footerNewsHeading}</div>
        <form
          className="lp-footer-news-form"
          onSubmit={(e) => { e.preventDefault(); }}
        >
          <input
            type="email" required
            className="lp-footer-news-input"
            placeholder={copy.footerNewsPlaceholder}
            aria-label="Email address"
          />
          <button type="submit" className="lp-footer-news-submit">
            {copy.footerNewsSubmit}
          </button>
        </form>
      </div>

      <div className="lp-footer-bot">
        <span>{copy.footerRegistration}</span>
        <span className="center">{copy.footerCopyright}</span>
        <span className="right">
          <a href="/privacy" className="lp-footer-legal-link">{copy.footerResources3}</a>
          <a href="/terms" className="lp-footer-legal-link">{copy.footerResources4}</a>
          <a href="https://www.linkedin.com/company/shopai" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
            <Linkedin size={18} />
          </a>
          <a href="#" aria-label="X / Twitter">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2H21.5l-7.62 8.713L23 22h-6.828l-5.363-6.63L4.7 22H1.44l8.15-9.315L1 2h6.996l4.852 6.027L18.244 2Zm-1.197 18h1.832L7.09 3.86H5.12L17.047 20Z" />
            </svg>
          </a>
          <a href="#" aria-label="YouTube">
            <Youtube size={18} />
          </a>
          <button
            type="button"
            onClick={onAdminClick}
            aria-label="Admin"
            className="lp-footer-admin-btn"
            data-testid="link-admin"
          >
            <Shield size={18} />
          </button>
        </span>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// InfoModal — surfaces the longer-form Platform / Modules descriptions when
// a footer link is clicked. Centred glass card, animated in/out, dismiss on
// backdrop click / Escape / X. Body is plain prose (no markdown/HTML) so we
// just render it inside a <p> with whitespace preserved.
// ─────────────────────────────────────────────────────────────────────────────

function InfoModal({ entry, onClose }: { entry: InfoEntry | null; onClose: () => void }) {
  // Lock body scroll + bind Escape to close while open. Cleaning up on unmount
  // matters if the user navigates away mid-open via Wouter — without the
  // cleanup, body would stay locked.
  useEffect(() => {
    if (!entry) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [entry, onClose]);

  if (!entry) return null;

  return (
    <div
      className="lp-info-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lp-info-title"
      onClick={onClose}
      data-testid="info-modal-overlay"
    >
      <div
        className={`lp-info-card${entry.image ? " lp-info-card--with-image" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="lp-info-close"
          onClick={onClose}
          aria-label="Close"
          data-testid="info-modal-close"
        >
          <span aria-hidden>×</span>
        </button>
        {entry.image && (
          // Edge-to-edge hero banner. The .lp-info-card--with-image modifier
          // pulls the inner padding off the top so the image meets the card
          // border cleanly; the close button sits over the image with a
          // translucent backdrop to stay legible against any photo content.
          <div className="lp-info-image-wrap">
            <img
              src={entry.image}
              alt={entry.imageAlt || `${entry.title} hero`}
              className="lp-info-image"
              loading="lazy"
              data-testid="info-modal-image"
            />
          </div>
        )}
        <div className="lp-info-content">
          <h2 id="lp-info-title" className="lp-info-title">{entry.title}</h2>
          <p className="lp-info-body">{entry.body}</p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const [, setLocation] = useLocation();
  const [lang, setLang] = useState<LandingLanguage>("en");
  // Footer Platform / Modules buttons open this modal. Null = closed.
  const [infoEntry, setInfoEntry] = useState<InfoEntry | null>(null);

  const copy = COPY[lang];
  const langMeta = useMemo(
    () => LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[3],
    [lang],
  );
  const dir = langMeta.dir;

  useEffect(() => {
    const token = localStorage.getItem("portal_token");
    const stored = localStorage.getItem("portalSession");
    if (token && stored) {
      try {
        const session = JSON.parse(stored);
        if (session.companySlug) setLocation(`/portal/${session.companySlug}`);
      } catch { /* noop */ }
    }
  }, [setLocation]);

  const go = (path: string) => () => setLocation(path);
  const bookDemo = () => {
    const subject = encodeURIComponent(copy.bookDemoSubject);
    const body = encodeURIComponent(copy.bookDemoBody);
    window.location.href = `mailto:info@shopai.uk?subject=${subject}&body=${body}`;
  };
  const seeHow = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="lp" dir={dir} lang={lang === "en-us" ? "en" : lang}>
      <style>{LANDING_STYLES}</style>

      <Nav
        lang={lang}
        onLangChange={setLang}
        onRegister={go("/register")}
        onBookDemo={bookDemo}
        onInfoOpen={setInfoEntry}
        copy={copy}
      />
      <Hero copy={copy} onBookDemo={bookDemo} onSeeHow={seeHow} />
      <StatsTicker copy={copy} />
      <ThreeStepWorkflow copy={copy} />
      <FullJourney copy={copy} />
      <IntegrationsStrip copy={copy} />

      <Footer copy={copy} lang={lang} onInfoOpen={setInfoEntry} onAdminClick={go("/admin/super")} />

      {/*
        Platform / Modules info modal. Mounted at the page root so it overlays
        every section regardless of stacking context inside the page chrome.
      */}
      <InfoModal entry={infoEntry} onClose={() => setInfoEntry(null)} />
    </div>
  );
}
